import { useEffect, useState } from 'react';
import { NavLink } from '@/components/Link';
import { useLocation } from '@tanstack/react-router';
import { BrandMark } from '../components/BrandMark';
import { Button } from '../components/Button';
import { DiscordIcon } from '../components/DiscordIcon';
import { Icon } from '../components/Icon';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button as UIButton } from '../components/ui/button';
import { DISCORD_URL } from '../lib/brand';
import { HeaderLanguageMenu } from '../i18n/HeaderLanguageMenu';
import { HeaderLanguageRow } from '../i18n/HeaderLanguageRow';
import { isTranslated } from '../i18n/localeHead';
import { m } from '../paraglide/messages';

/** The primary links, shared with the landing hero's in-frame nav row. A
 *  function so the labels are read in the locale of the render. */
export const navLinks = (): Array<{ to: string; label: string; external?: boolean }> => [
  { to: '/catalog', label: m.nav_api_prices() },
  // The network's own record, in the nav because it is the evidence behind
  // every reliability claim the site makes. /providers stays a footer link.
  { to: '/usage', label: m.nav_network() },
  { to: 'https://getanyapi.com/docs', label: m.nav_docs(), external: true },
  // Affiliates earns a tab rather than a footer link because the people it is
  // for arrive from someone else's post and never go looking for it.
  { to: '/affiliates', label: m.nav_affiliates() },
];

const navLinkClass = (isActive: boolean) =>
  `text-sm font-medium ${isActive ? 'text-text' : 'text-text-2 hover:text-text'}`;

/**
 * The nav's one auth action, static and Clerk-free.
 *
 * It used to call `useAuth()` to choose between "Dashboard" and a pair of Clerk
 * modal buttons, which meant every prerendered marketing page loaded Clerk just
 * to label a button - and the label still could not be trusted before Clerk
 * resolved, so the markup had to be gated on `isLoaded` anyway.
 *
 * There is deliberately no "Sign in" beside it. The nav is read by visitors who
 * do not have an account yet; a returning one reaches the form through the
 * "Already have an account?" link Clerk renders inside `/sign-up`, and `/sign-in`
 * stays a real route for that link, for bookmarks, and for Clerk's own redirects
 * (`ClerkScope` names it as `signInUrl`). Anyone already signed in who lands on
 * either page is sent straight to the dashboard.
 *
 * Being static also makes this SSR-deterministic by construction - there is no
 * client-only branch left to mismatch.
 */
const NavAuthButtons = () => (
  <Button as="a" to="/sign-up" variant="primary" className="h-8 px-3.5 text-14">
    {m.nav_get_started()}
  </Button>
);

/**
 * On the landing page the bar is fixed rather than sticky, so it takes no
 * layout space above the hero frame, and it is redundant while the frame is on
 * screen: the frame carries its own nav row. So it starts veiled and reveals
 * once `#hero-frame` (rendered by the hero) has left the viewport.
 *
 * SSR and the first client render both return `true` on the landing page, so the
 * veiled markup is deterministic; the observer only ever lifts the veil. When
 * there is no hero frame to watch - any other page, or a landing variant that
 * stops rendering one - the bar is visible.
 */
const useVeiled = (landing: boolean): boolean => {
  const [veiled, setVeiled] = useState(landing);

  useEffect(() => {
    if (!landing) {
      setVeiled(false);
      return;
    }
    const frame = document.getElementById('hero-frame');
    if (frame === null || typeof IntersectionObserver === 'undefined') {
      setVeiled(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setVeiled(entry?.isIntersecting !== false),
      { threshold: 0 },
    );
    observer.observe(frame);
    return () => observer.disconnect();
  }, [landing]);

  return veiled;
};

export interface NavProps {
  /** The landing page hides the bar until its hero frame scrolls away. */
  landing?: boolean;
}

/** Sticky top navigation bar with a collapsible menu below `md`. */
export const Nav = ({ landing = false }: NavProps) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  // Route changes (including ones triggered outside the menu) collapse it.
  const { pathname } = useLocation();
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setOpen(false);
  }
  const veiled = useVeiled(landing);
  const translated = isTranslated(pathname);

  return (
    <nav
      className={`${landing ? 'fixed inset-x-0' : 'sticky'} top-0 z-50 border-b border-rule bg-bg transition-[opacity,transform] duration-150 ${
        veiled ? 'nav-veiled' : ''
      }`}
    >
      <div className="nav-inner relative mx-auto flex h-14 max-w-295 items-center gap-3 px-4 sm:px-8">
        <NavLink
          to="/"
          onClick={close}
          className="flex items-center gap-2 text-18 font-bold tracking-[-0.02em]"
        >
          <BrandMark size={22} />
          <span>
            <span className="text-accent">any</span>api
          </span>
        </NavLink>
        <div className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-7 md:flex">
          {navLinks().map((l) =>
            l.external ? (
              <a key={l.to} href={l.to} className={navLinkClass(false)}>
                {l.label}
              </a>
            ) : (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => navLinkClass(isActive)}>
                {l.label}
              </NavLink>
            ),
          )}
        </div>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            aria-label={m.nav_join_discord()}
            // The language switcher takes this room between md and lg.
            className={`hidden text-text-2 hover:text-text ${translated ? 'lg:inline-flex' : 'md:inline-flex'}`}
          >
            <DiscordIcon size={18} />
          </a>
          <HeaderLanguageMenu className="hidden md:inline-flex" />
          <ThemeToggle />
          <NavAuthButtons />
          <UIButton
            type="button"
            variant="line"
            size="icon"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? m.nav_close_menu() : m.nav_open_menu()}
            aria-expanded={open}
            className="size-8 rounded-8 text-text-2 hover:text-text md:hidden"
          >
            <Icon name={open ? 'x' : 'menu'} size={16} />
          </UIButton>
        </div>
      </div>

      {open ? (
        <div className="border-t border-rule bg-bg md:hidden">
          <div className="nav-inner mx-auto flex max-w-295 flex-col px-4 py-2 sm:px-8">
            {navLinks().map((l) =>
              l.external ? (
                <a key={l.to} href={l.to} className="py-3 text-15 font-medium text-text-2">
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={close}
                  className={({ isActive }) =>
                    `py-3 text-15 font-medium ${isActive ? 'text-text' : 'text-text-2'}`
                  }
                >
                  {l.label}
                </NavLink>
              ),
            )}
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              onClick={close}
              className="flex items-center gap-2 py-3 text-15 font-medium text-text-2"
            >
              <DiscordIcon size={17} />
              Discord
            </a>
            <HeaderLanguageRow />
          </div>
        </div>
      ) : null}
    </nav>
  );
};
